import { useState, useMemo, useCallback } from "react";
import {
  createSearchIndex,
  addToSearchIndex,
  search,
  SearchResult,
  SearchIndex,
} from "../../utils/docs/searchIndex";
import { ContentPage } from "../../utils/docs/contentLoader";
import { loadMarkdownContent } from "../../utils/docs/contentLoader";

export function useDocsSearch(pages: ContentPage[]) {
  const [searchIndex, setSearchIndex] = useState<SearchIndex | null>(null);
  const [isIndexing, setIsIndexing] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);

  // Build search index from all pages
  const buildIndex = useCallback(async () => {
    if (isIndexing || searchIndex) return;

    setIsIndexing(true);
    const index = createSearchIndex();

    try {
      // Load all markdown files and add to index
      await Promise.all(
        pages.map(async (page) => {
          try {
            const content = await loadMarkdownContent(page.filePath);
            addToSearchIndex(index, page, content);
          } catch (error) {
            console.error(`Failed to index ${page.path}:`, error);
          }
        }),
      );

      setSearchIndex(index);
    } catch (error) {
      console.error("Failed to build search index:", error);
    } finally {
      setIsIndexing(false);
    }
  }, [pages, isIndexing, searchIndex]);

  // Perform search
  const performSearch = useCallback(
    (searchQuery: string) => {
      setQuery(searchQuery);

      if (!searchIndex || !searchQuery.trim()) {
        setResults([]);
        return;
      }

      const searchResults = search(searchIndex, searchQuery, 10);
      setResults(searchResults);
    },
    [searchIndex],
  );

  // Initialize index on mount
  useMemo(() => {
    if (pages.length > 0 && !searchIndex && !isIndexing) {
      buildIndex();
    }
  }, [pages, searchIndex, isIndexing, buildIndex]);

  return {
    query,
    results,
    isIndexing,
    search: performSearch,
    buildIndex,
  };
}
